/**
 * Transforms the ThreeG API response rateMetadata to the UI-expected
 * format (buyRateTotal, sellRateTotal, etc.) and adds ThreeG-specific
 * fields for the tooltip.
 */
export function transformLTLThreeGQuote(
  quote: Record<string, any>
): Record<string, any> {
  if (!quote.rateMetadata || quote.rateMetadata.netTotal === undefined) {
    return quote;
  }

  const threeGRateMetadata = quote.rateMetadata;
  const netTotal = threeGRateMetadata.netTotal || 0;
  const netCost = threeGRateMetadata.netCost || 0;
  const netFreight = threeGRateMetadata.netFreight || 0;
  const netAccessorials = threeGRateMetadata.netAccessorials || 0;
  const profitMarkup = threeGRateMetadata.profitMarkup || 0;
  const marginPercentage = threeGRateMetadata.marginPercentage || '0%';
  const fuelRate = quote.fuelRate || 0;

  const fuelSurchargeAccessorial = quote.accessorials?.find(
    (acc: any) => acc.code === 'FSC' || acc.name === 'FUEL SURCHARGE'
  );
  const fuelSurchargeAmount =
    fuelSurchargeAccessorial?.value?.netAmt || fuelRate || 0;

  const nonFuelAccessorials = Math.max(
    0,
    netAccessorials - fuelSurchargeAmount
  );

  const sellRateTotal = netTotal;
  const buyRateTotal = netCost;

  const roundToTwo = (n: number) => Math.round(n * 100) / 100;

  const buyRateFreightCharges =
    sellRateTotal > 0
      ? roundToTwo((netFreight / sellRateTotal) * buyRateTotal)
      : netFreight;
  const buyFuelSurcharge =
    sellRateTotal > 0
      ? roundToTwo((fuelSurchargeAmount / sellRateTotal) * buyRateTotal)
      : fuelSurchargeAmount;
  const buyAccessorialTotalAmount =
    sellRateTotal > 0
      ? roundToTwo((nonFuelAccessorials / sellRateTotal) * buyRateTotal)
      : nonFuelAccessorials;

  return {
    ...quote,
    rateMetadata: {
      buyRateFreightCharges,
      buyFuelSurcharge,
      buyAccessorialTotalAmount,
      buyRateTotal,
      buyFuelPercentage: 0,
      sellRateFreightCharges: netFreight,
      sellFuelSurcharge: fuelSurchargeAmount,
      sellAccessorialTotalAmount: nonFuelAccessorials,
      sellRateTotal,
      sellFuelPercentage: 0,
      accessorialPrices: null,
      margins: [],
      isThreeG: true,
      threeGNetCost: netCost,
      threeGNetFreight: netFreight,
      threeGNetAccessorials: netAccessorials,
      threeGProfitMarkup: profitMarkup,
      threeGMarginPercentage: marginPercentage,
    },
  };
}
